'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/#about' },
  {
    label: 'Services',
    href: '/#services',
    dropdown: [
      { label: 'Quantum Karmic Healing', href: '/#services' },
      { label: 'Clinical Hypnotherapy', href: '/#services' },
      { label: 'Past Life Regression', href: '/services/past-life-regression' },
      { label: 'Family Constellation', href: '/#services' },
      { label: 'Akashic Records Reading', href: '/services/akashic-records' },
      { label: 'Reiki Healing', href: '/#services' },
      { label: 'Tarot Guidance', href: '/#services' },
      { label: 'Emotional Wellness', href: '/#services' },
    ],
  },
  { label: 'Courses', href: '/#courses' },
  { label: 'Testimonials', href: '/#testimonials' },
  { label: 'FAQ', href: '/#faq' },
  { label: 'Contact', href: '/#contact' },
];

export default function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'py-3 shadow-sm' : 'py-4'}`}
      style={{
        background: scrolled ? 'rgba(13, 8, 24, 0.97)' : 'rgba(13, 8, 24, 0.85)',
        backdropFilter: 'blur(16px)',
        borderBottom: scrolled ? '1px solid rgba(201,168,76,0.20)' : 'none',
        boxShadow: scrolled ? '0 2px 30px rgba(0,0,0,0.4)' : 'none',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2.5 group">
          <div className="relative" style={{ width: '44px', height: '44px' }}>
            <Image
              src="/assets/images/WhatsApp_Image_2026-07-17_at_9.33.22_PM__1_-1785064751342.jpeg"
              alt="Shreevidha Healing Hub Logo"
              fill
              className="object-contain rounded-full"
              priority
              style={{ border: '1.5px solid rgba(201,168,76,0.4)' }}
            />
          </div>
          <div>
            <span
              className="font-serif text-lg font-semibold tracking-wide"
              style={{
                background: 'linear-gradient(135deg, #c9a84c 0%, #e8c96a 50%, #c9a84c 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              ShreeVidha
            </span>
            <span className="block text-[10px] text-[#9b7fe8]/70 tracking-[0.2em] uppercase -mt-0.5">
              Healing Hub
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-6">
          {navLinks?.map((link) =>
            link?.dropdown ? (
              <div
                key={link?.label}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <button className="nav-link text-sm tracking-widest uppercase font-medium flex items-center gap-1">
                  {link?.label}
                  <span className="text-[#c9a84c]/60 text-xs">▾</span>
                </button>
                {servicesOpen && (
                  <div
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 rounded-2xl overflow-hidden z-50"
                    style={{
                      background: 'rgba(13, 8, 24, 0.98)',
                      border: '1px solid rgba(201,168,76,0.25)',
                      boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
                      backdropFilter: 'blur(20px)',
                    }}
                  >
                    {link?.dropdown?.map((item) => (
                      <Link
                        key={item?.label}
                        href={item?.href}
                        className="block px-4 py-3 text-xs text-[#e8dff5]/75 hover:text-[#c9a84c] hover:bg-[#c9a84c]/05 transition-all duration-200 border-b border-[#c9a84c]/08 last:border-0"
                      >
                        {item?.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link?.label}
                href={link?.href}
                className="nav-link text-sm tracking-widest uppercase font-medium"
              >
                {link?.label}
              </Link>
            )
          )}
        </div>

        {/* CTA */}
        <div className="hidden lg:flex items-center gap-3">
          <a
            href="https://chat.whatsapp.com/JBAssqjgZSQ89CRz3EMpqn"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold tracking-wide transition-all duration-300 hover:scale-105"
            style={{
              background: 'rgba(37, 211, 102, 0.10)',
              border: '1.5px solid rgba(37, 211, 102, 0.4)',
              color: '#25D366',
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Join Community
          </a>
          <a
            href="https://cal.com/shruti-g6cvvv/30min?overlayCalendar=true"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold text-xs"
          >
            Book Session
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden text-[#c9a84c] p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <div className="w-6 flex flex-col gap-1.5">
            <span className={`block h-0.5 bg-[#c9a84c] transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block h-0.5 bg-[#c9a84c] transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block h-0.5 bg-[#c9a84c] transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </div>
        </button>
      </div>
      {/* Mobile Menu */}
      <div className={`lg:hidden transition-all duration-300 overflow-hidden ${menuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div
          className="border-t px-6 py-4 flex flex-col gap-1"
          style={{ background: 'rgba(13, 8, 24, 0.98)', borderColor: 'rgba(201,168,76,0.15)' }}
        >
          {navLinks?.map((link) => (
            <div key={link?.label}>
              <Link
                href={link?.href}
                className="block text-sm tracking-widest uppercase py-2.5 border-b text-[#e8dff5]/75 hover:text-[#c9a84c] transition-colors duration-200"
                style={{ borderColor: 'rgba(201,168,76,0.10)' }}
                onClick={() => setMenuOpen(false)}
              >
                {link?.label}
              </Link>
              {link?.dropdown && (
                <div className="pl-4 py-1 flex flex-col gap-1">
                  {link?.dropdown?.map((item) => (
                    <Link
                      key={item?.label}
                      href={item?.href}
                      className="text-xs text-[#c4b5fd]/60 hover:text-[#c9a84c] py-1.5 transition-colors duration-200"
                      onClick={() => setMenuOpen(false)}
                    >
                      ↳ {item?.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="flex flex-col gap-3 mt-3">
            <a
              href="https://chat.whatsapp.com/JBAssqjgZSQ89CRz3EMpqn"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-xs font-semibold tracking-wide"
              style={{ background: 'rgba(37,211,102,0.10)', border: '1.5px solid rgba(37,211,102,0.4)', color: '#25D366' }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              Join Community
            </a>
            <a
              href="https://cal.com/shruti-g6cvvv/30min?overlayCalendar=true"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold text-xs text-center"
            >
              Book Session
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}
